import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import logger from "@/utils/logger";
import { toast } from "sonner";
import adminApi from "./adminApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Star, Trash2, Pencil, Plus } from "lucide-react";

const emptyForm = { customer_name: "", event_type: "Wedding", rating: 5, comment: "" };

const TestimonialForm = ({ initial, onSave, onCancel, saving }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(initial || emptyForm);
  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  return (
    <div className="space-y-4">
      <div>
        <Label className="mb-1 block">Customer Name</Label>
        <Input value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} data-testid="testimonial-name-input" />
      </div>
      <div>
        <Label className="mb-1 block">Event Type</Label>
        <Select value={form.event_type} onValueChange={(v) => update("event_type", v)}>
          <SelectTrigger data-testid="testimonial-event-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["Wedding", "Corporate Event", "Birthday Party", "Traditional Ceremony", "Private Dinner", "Other"].map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="mb-1 block">Rating</Label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <button key={i} type="button" onClick={() => update("rating", i)} data-testid={`rating-star-${i}`}>
              <Star className={`w-6 h-6 cursor-pointer ${i <= form.rating ? "text-[#F2CC8F] fill-current" : "text-gray-300"}`} />
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="mb-1 block">Comment</Label>
        <Textarea value={form.comment} onChange={(e) => update("comment", e.target.value)} rows={3} data-testid="testimonial-comment-input" />
      </div>
      <div className="flex gap-3 pt-2">
        <Button onClick={() => onSave(form)} disabled={saving || !form.customer_name || !form.comment} data-testid="testimonial-save-btn" className="bg-[#2D6A4F] hover:bg-[#245a42] text-white">
          {saving ? "Saving..." : "Save"}
        </Button>
        {onCancel && <Button variant="outline" onClick={onCancel}>Cancel</Button>}
      </div>
    </div>
  );
};

const TestimonialsManagement = () => {
  const { t } = useTranslation();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await adminApi.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      logger.error("Error fetching testimonials:", error);
    } finally {
      setLoading(false);
    }
  }, [API, adminApi]);

  useEffect(() => { fetchTestimonials(); }, [fetchTestimonials]);

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      await adminApi.post(`${API}/testimonials`, form);
      toast.success("Testimonial added!");
      setShowAdd(false);
      fetchTestimonials();
    } catch { toast.error("Failed to add testimonial"); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    try {
      await adminApi.put(`${API}/testimonials/${editingId}`, form);
      toast.success("Testimonial updated!");
      setEditingId(null);
      fetchTestimonials();
    } catch { toast.error("Failed to update testimonial"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("admin.dashboard.confirmDelete.testimonial"))) return;
    try {
      await adminApi.delete(`${API}/testimonials/${id}`);
      toast.success(t("admin.dashboard.toast.testimonialDeleted"));
      fetchTestimonials();
    } catch { toast.error(t("admin.dashboard.toast.testimonialDeleteError")); }
  };

  return (
    <div data-testid="testimonials-management" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#3D405B] font-['Playfair_Display']">{t("admin.dashboard.testimonials")}</h1>
          <p className="text-[#6B705C]">{t("admin.dashboard.manageTestimonials")}</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button className="bg-[#2D6A4F] hover:bg-[#245a42] text-white" data-testid="add-testimonial-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Testimonial
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Testimonial</DialogTitle></DialogHeader>
            <TestimonialForm onSave={handleCreate} saving={saving} />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner"></div></div>
      ) : (
        <div className="space-y-4">
          {testimonials.map((testimonial) => (
            <Card key={testimonial.id} className="bg-white border-0 shadow-sm" data-testid={`admin-testimonial-${testimonial.id}`}>
              <CardContent className="p-6">
                {editingId === testimonial.id ? (
                  <TestimonialForm
                    initial={{ customer_name: testimonial.customer_name, event_type: testimonial.event_type, rating: testimonial.rating, comment: testimonial.comment }}
                    onSave={handleUpdate}
                    onCancel={() => setEditingId(null)}
                    saving={saving}
                  />
                ) : (
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star key={`star-${testimonial.id}-${i}`} className={`w-4 h-4 ${i < testimonial.rating ? "text-[#F2CC8F] fill-current" : "text-gray-300"}`} />
                        ))}
                      </div>
                      <p className="text-[#3D405B] italic mb-4">&ldquo;{testimonial.comment}&rdquo;</p>
                      <p className="font-medium text-[#3D405B]">{testimonial.customer_name}</p>
                      <p className="text-sm text-[#6B705C]">{testimonial.event_type}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(testimonial.id)} data-testid={`edit-testimonial-${testimonial.id}`}>
                        <Pencil className="w-4 h-4 text-[#3D405B]" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(testimonial.id)} className="text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TestimonialsManagement;
